import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ArrowRight, Shield, Users, Handshake, Store } from 'lucide-react'
import { SEOHead } from '@/components/SEOHead'
import { LocalizedLink } from '@/components/LocalizedLink'
import type { SupportedLanguage } from '@/i18n'

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
}

const audiences = [
  { titleKey: 'academy.audiencePolice', descKey: 'academy.audiencePoliceDesc', icon: Shield },
  { titleKey: 'academy.audienceIntegrators', descKey: 'academy.audienceIntegratorsDesc', icon: Users },
  { titleKey: 'academy.audiencePartners', descKey: 'academy.audiencePartnersDesc', icon: Handshake },
  { titleKey: 'academy.audienceDistributors', descKey: 'academy.audienceDistributorsDesc', icon: Store },
]

const formats = [
  { num: '01', titleKey: 'academy.formatVideo', descKey: 'academy.formatVideoDesc', icon: '/media/academy/corsi-video.svg' },
  { num: '02', titleKey: 'academy.formatClassroom', descKey: 'academy.formatClassroomDesc', icon: '/media/academy/corsi-in-aula.svg' },
  { num: '03', titleKey: 'academy.formatWebinar', descKey: 'academy.formatWebinarDesc', icon: '/media/academy/corsi-webinar.svg' },
  { num: '04', titleKey: 'academy.formatDocs', descKey: 'academy.formatDocsDesc', icon: '/media/academy/document.svg' },
]

export function AcademyPage() {
  const { t, i18n } = useTranslation()
  const lang = (i18n.language || 'it') as SupportedLanguage

  return (
    <>
      <SEOHead titleKey="seo.academy.title" descriptionKey="seo.academy.description" />

      {/* ── 1. Hero — Cinematic left-aligned ── */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/media/academy/selea-academy-hero.jpg"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/70 to-primary/30" />
        </div>

        <div className="container-main relative z-10 py-20">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-accent font-semibold text-sm uppercase tracking-wider mb-4"
            >
              {t('academy.eyebrow')}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-5xl font-bold text-white leading-tight mb-6"
            >
              {t('academy.heroTitle')}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg text-white/80 mb-8 leading-relaxed"
            >
              {t('academy.heroSubtitle')}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <a
                href="https://www.selea.com/selea-academy/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                {t('academy.heroCta')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </a>
              <LocalizedLink
                to={lang === 'en' ? '/contacts' : '/contatti'}
                className="btn-outline border-white text-white hover:bg-white hover:text-primary"
              >
                {t('academy.ctaSecondary')}
              </LocalizedLink>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 2. Philosophy + Value Props ── */}
      <section className="relative section-padding bg-primary overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-accent/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-accent/5 rounded-full blur-3xl pointer-events-none" />

        <div className="container-main relative z-10">
          {/* Statement */}
          <div className="max-w-3xl mx-auto text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-3xl md:text-4xl font-bold text-white mb-6"
            >
              {t('academy.philosophyStatement')}
            </motion.h2>
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="w-20 h-1 bg-accent mx-auto mb-6 origin-center"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-white/70 text-lg leading-relaxed"
            >
              {t('academy.philosophyDesc')}
            </motion.p>
          </div>

          {/* 3 value prop cards — frosted glass */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {([
              { titleKey: 'academy.valueFree', descKey: 'academy.valueFreeDesc', num: '01' },
              { titleKey: 'academy.valueCertified', descKey: 'academy.valueCertifiedDesc', num: '02' },
              { titleKey: 'academy.valueContinuous', descKey: 'academy.valueContinuousDesc', num: '03' },
            ] as const).map((item, index) => (
              <motion.div
                key={item.titleKey}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="relative rounded-2xl bg-white/5 border border-white/10 p-8 backdrop-blur-sm overflow-hidden group"
              >
                {/* Large ghost number */}
                <span className="absolute -top-3 -right-2 text-[6rem] font-black leading-none text-white/[.04] select-none pointer-events-none">
                  {item.num}
                </span>
                <div className="relative z-10">
                  <h3 className="text-xl font-bold text-white mb-2">{t(item.titleKey)}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{t(item.descKey)}</p>
                </div>
                {/* Hover accent bar */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. Target Audiences — Bento Grid ── */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-3xl md:text-4xl font-bold mb-4"
            >
              {t('academy.audiencesTitle')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-text-muted max-w-2xl mx-auto"
            >
              {t('academy.audiencesSubtitle')}
            </motion.p>
          </div>

          {/* Bento: Police full-width top, 2 mid, Distributors full-width bottom */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
            {audiences.map((aud, index) => {
              const Icon = aud.icon
              const isWide = index === 0 || index === 3
              return (
                <motion.div
                  key={aud.titleKey}
                  custom={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  className={`group relative rounded-2xl bg-primary overflow-hidden ${isWide ? 'lg:col-span-2' : ''}`}
                >
                  {/* Ghost number */}
                  <span className="absolute top-4 right-6 text-[7rem] font-black leading-none text-white/[.04] select-none pointer-events-none">
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  <div className={`relative z-10 p-8 lg:p-10 ${isWide ? 'flex flex-col md:flex-row md:items-center gap-6' : ''}`}>
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-14 h-14 rounded-xl bg-accent/15 flex items-center justify-center ${isWide ? '' : 'mb-5'}`}>
                      <Icon className="w-7 h-7 text-accent" />
                    </div>

                    <div className={isWide ? 'flex-1' : ''}>
                      <h3 className="text-xl lg:text-2xl font-bold text-white mb-2">
                        {t(aud.titleKey)}
                      </h3>
                      <p className={`text-white/60 leading-relaxed text-sm ${isWide ? 'max-w-2xl' : ''}`}>
                        {t(aud.descKey)}
                      </p>
                    </div>
                  </div>

                  {/* Hover accent bar at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── 4. Course Formats — Stepped cards ── */}
      <section className="section-padding bg-surface-light">
        <div className="container-main">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-3xl md:text-4xl font-bold mb-4"
            >
              {t('academy.formatsTitle')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-text-muted max-w-2xl mx-auto"
            >
              {t('academy.formatsSubtitle')}
            </motion.p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {formats.map((fmt, index) => (
              <motion.div
                key={fmt.titleKey}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="group relative bg-white rounded-2xl overflow-hidden border border-surface-border hover:border-accent/30 hover:shadow-xl transition-all duration-500"
                style={{ marginTop: `${index * 16}px` }}
              >
                {/* Top accent gradient bar */}
                <div className="h-1 bg-gradient-to-r from-accent to-accent/40" />

                <div className="p-7">
                  {/* Number + icon row */}
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-4xl font-black text-accent/15 select-none">
                      {fmt.num}
                    </span>
                    <div className="w-12 h-12 rounded-xl bg-surface-light flex items-center justify-center group-hover:bg-accent/10 transition-colors duration-300">
                      <img
                        src={fmt.icon}
                        alt=""
                        className="w-6 h-6"
                        loading="lazy"
                      />
                    </div>
                  </div>

                  <h3 className="text-lg font-bold mb-2 group-hover:text-accent transition-colors duration-300">
                    {t(fmt.titleKey)}
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed">
                    {t(fmt.descKey)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. CTA — Split cinematic banner ── */}
      <section className="relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[450px]">
          {/* Left — image */}
          <div className="relative min-h-[300px] lg:min-h-0">
            <img
              src="/media/academy/selea-academy-banner.jpg"
              alt="Selea Academy"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            {/* Fade edge on desktop for seamless blend */}
            <div className="hidden lg:block absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-primary to-transparent" />
            {/* Fade bottom on mobile */}
            <div className="lg:hidden absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-primary to-transparent" />
          </div>

          {/* Right — content */}
          <div className="relative bg-primary flex items-center">
            {/* Ghost decorative */}
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-accent/5 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 p-10 lg:p-16 max-w-lg">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-3xl md:text-4xl font-bold text-white mb-4"
              >
                {t('academy.ctaTitle')}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-white/70 leading-relaxed mb-8"
              >
                {t('academy.ctaDesc')}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex flex-wrap gap-4"
              >
                <a
                  href="https://www.selea.com/selea-academy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  {t('academy.ctaPrimary')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
                <LocalizedLink
                  to={lang === 'en' ? '/contacts' : '/contatti'}
                  className="btn-outline border-white text-white hover:bg-white hover:text-primary"
                >
                  {t('academy.ctaSecondary')}
                </LocalizedLink>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
